import { ReactElement } from 'react';
import {
  AccountTreeOutlined,
  AssignmentLateOutlined,
  GroupAddOutlined,
  HistoryRounded,
  MobileFriendlyOutlined,
  PersonPinOutlined,
  SettingsOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import MonitorOutlinedIcon from '@mui/icons-material/MonitorOutlined';
import ConversionPathIcon from 'shared/components/icons/conversion-path-icon';
import IntegrationsIcon from 'shared/components/icons/integrations-icon';
import Permission from 'shared/features/auth/authorization/enums/permission';
import PermissionGroup from 'shared/features/auth/authorization/enums/permission-group';

export type PermissionGroupSetting = {
  permissionGroup: PermissionGroup;
  permissions: Array<Permission>;
  icon: ReactElement;
};

export const DefaultPermissionGroupSettingsApp: Array<PermissionGroupSetting> = [
  {
    permissionGroup: PermissionGroup.App,
    permissions: [
      Permission.AppAccess,
      Permission.ReadOverview,
      Permission.MuteApp,
      Permission.UserPreferences,
      Permission.RequestTemporaryChannelAccess,
    ],
    icon: <MobileFriendlyOutlined />,
  },
  {
    permissionGroup: PermissionGroup.AppMonitoring,
    permissions: [Permission.ReadFaults, Permission.ReadFaultsOnUnlinkedDevices],
    icon: <WarningAmberRounded />,
  },
];

export const DefaultPermissionGroupSettingsPortal: Array<PermissionGroupSetting> = [
  {
    permissionGroup: PermissionGroup.ResidentsSettings,
    permissions: [
      Permission.ReadResidentsSettings,
      Permission.UpdateResidentsSettings,
      Permission.ReadResidentsChangeLog,
      Permission.UpdateResidentAbsence,
    ],
    icon: <PersonPinOutlined />,
  },
  {
    permissionGroup: PermissionGroup.TemporaryAccess,
    permissions: [Permission.ManageTemporaryAccess],
    icon: <GroupAddOutlined />,
  },
  {
    permissionGroup: PermissionGroup.History,
    permissions: [Permission.ReadHistory, Permission.ReadHistoryDetails],
    icon: <HistoryRounded />,
  },
  {
    permissionGroup: PermissionGroup.Scenario,
    permissions: [Permission.LinkScenario],
    icon: <ConversionPathIcon />,
  },
  {
    permissionGroup: PermissionGroup.User,
    permissions: [Permission.CreateRole, Permission.DeleteRole, Permission.ReadRole, Permission.UpdateRole],
    icon: <BadgeOutlinedIcon />,
  },
  {
    permissionGroup: PermissionGroup.OrganisationUnit,
    permissions: [
      Permission.CreateOrganisationUnit,
      Permission.DeleteOrganisationUnit,
      Permission.ReadOrganisationUnit,
      Permission.UpdateOrganisationUnit,
    ],
    icon: <AccountTreeOutlined />,
  },
  {
    permissionGroup: PermissionGroup.Integrations,
    permissions: [
      Permission.CreateDevice,
      Permission.ReadDevice,
      Permission.UpdateDevice,
      Permission.DeleteDevice,
      Permission.LinkDevice,
      Permission.ReadSystems,
      Permission.CreateSystems,
      Permission.UpdateSystems,
      Permission.DeleteSystems,
      Permission.ReadGateways,
      Permission.CreateGateways,
      Permission.UpdateGateways,
      Permission.DeleteGateways,
      Permission.ReadConnectors,
      Permission.CreateConnectors,
      Permission.UpdateConnectors,
      Permission.DeleteConnectors,
      Permission.SendTestEvent,
    ],
    icon: <IntegrationsIcon />,
  },
  {
    permissionGroup: PermissionGroup.Settings,
    permissions: [
      Permission.SettingsExplanationLink,
      Permission.SettingsEmergencyAlarm,
      Permission.SettingsMalfunctionNotifications,
      Permission.SettingsWanderingBoundaries,
      Permission.CareConnectReadAndUpdateSettings,
    ],
    icon: <SettingsOutlined />,
  },
  {
    permissionGroup: PermissionGroup.Alerts,
    permissions: [Permission.ReadAlertsHistory],
    icon: <AssignmentLateOutlined />,
  },
];

export const DefaultPermissionGroupSettingsCentralSupportPortal: Array<PermissionGroupSetting> = [
  {
    permissionGroup: PermissionGroup.CentralSupportPortal,
    permissions: [Permission.CentralSupportPortalAccess],
    icon: <MonitorOutlinedIcon />,
  },
  {
    permissionGroup: PermissionGroup.CentralSupportMonitoring,
    permissions: [Permission.CentralSupportReadFaults],
    icon: <WarningAmberRounded />,
  },
];

export const DefaultPermissionGroupSettingsInsightsPortal: Array<PermissionGroupSetting> = [
  {
    permissionGroup: PermissionGroup.InsightsPortal,
    permissions: [Permission.InsightsPortalAccess],
    icon: <MonitorOutlinedIcon />,
  },
];
